import { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MapPin, Mail, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null); // "success" | "error" | "sending"

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: form.message,
      });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setStatus(null), 5000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t.contact.address,
      value: "Zandvlietstraat 68, 2020 Antwerpen",
    },
    {
      icon: Mail,
      label: t.contact.email,
      value: "info@rentcoming.com",
    },
    {
      icon: Phone,
      label: t.contact.phone,
      value: "+32 489 30 27 85",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-gray-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Info */}
          <div className="space-y-8">
            <div className="inline-block">
              <span
                data-testid="contact-tag"
                className="text-xs font-bold tracking-[0.2em] uppercase text-red-500 font-body"
              >
                {t.contact.tag}
              </span>
              <div className="w-12 h-0.5 bg-red-500 mt-1" />
            </div>

            <h2
              data-testid="contact-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-none text-white font-heading"
            >
              {t.contact.title}
            </h2>

            <p
              data-testid="contact-subtitle"
              className="text-base sm:text-lg leading-relaxed text-gray-400 font-body"
            >
              {t.contact.subtitle}
            </p>

            <div className="space-y-6 pt-8">
              {contactInfo.map((item, idx) => (
                <div
                  key={item.label}
                  data-testid={`contact-info-${idx}`}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 bg-red-600 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-body mb-1">
                      {item.label}
                    </div>
                    <div className="text-white font-medium font-body">
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-5"
            >
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t.contact.namePlaceholder}
                required
                data-testid="contact-name-input"
                className="rounded-none bg-transparent border-gray-600 text-white placeholder:text-gray-500 h-12 font-body focus:border-red-500 focus:ring-0"
              />
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder={t.contact.emailPlaceholder}
                required
                data-testid="contact-email-input"
                className="rounded-none bg-transparent border-gray-600 text-white placeholder:text-gray-500 h-12 font-body focus:border-red-500 focus:ring-0"
              />
              <Input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t.contact.phonePlaceholder}
                data-testid="contact-phone-input"
                className="rounded-none bg-transparent border-gray-600 text-white placeholder:text-gray-500 h-12 font-body focus:border-red-500 focus:ring-0"
              />
              <Textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder={t.contact.messagePlaceholder}
                required
                rows={5}
                data-testid="contact-message-input"
                className="rounded-none bg-transparent border-gray-600 text-white placeholder:text-gray-500 font-body resize-none focus:border-red-500 focus:ring-0"
              />

              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={status === "sending"}
                className="w-full rounded-none bg-red-600 hover:bg-red-700 text-white font-bold text-base h-12 font-body disabled:opacity-50"
              >
                {status === "sending" ? t.contact.sending : t.contact.sendBtn}
              </Button>

              {status === "success" && (
                <p
                  data-testid="contact-success-msg"
                  className="text-green-400 text-sm font-body"
                >
                  {t.contact.successMsg}
                </p>
              )}
              {status === "error" && (
                <p
                  data-testid="contact-error-msg"
                  className="text-red-400 text-sm font-body"
                >
                  {t.contact.errorMsg}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
